import {useEffect, useRef} from 'react';
import scheduleAnimationCallback from '../scheduleAnimationCallback';

const TAB = 'Tab';
const ESC = 'Escape';

function noop() {}

/**
 * Wrapping in requestAnimationFrame improves performance since element.focus
 * forces layout/reflow
 * @see https://gist.github.com/paulirish/5d52fb081b3570c81e3a
 */
function focusElement(el) {
  scheduleAnimationCallback(() => {
    if (el?.focus) {
      el.focus();
    }
  });
}

/**
 * Returns the last entry in a Map
 */
function getLastEntry(m) {
  return Array.from(m.values()).pop();
}

const map = new Map();
let iterator = 0;

export default function useFocusTrap(opts) {
  // Iterating id
  const id = useRef(iterator++);

  const savedCallback = useRef();

  // Options
  const {
    active,
    firstFocusable: first,
    lastFocusable: last,
    onEscape = noop,
  } = opts;

  // Set activeState when active is set and the first focusable is set.
  // We use this value instead of the passed in the active state because the trap should not become truly active until
  // its first and last focusable refs are also set, which may not always occur at the same time when the active state becomes true.
  const activeState = active && first !== null;

  // Pattern to split effects and use a mutable savedCallback was found here: https://overreacted.io/making-setinterval-declarative-with-react-hooks/
  useEffect(() => {
    // Focus initially on first and then only when active state is changed
    savedCallback.current = () => focusElement(first);
  });

  useEffect(() => {
    // Refocus when activeState changes
    if (activeState) {
      savedCallback.current();
    }
  }, [activeState]);

  useEffect(() => {
    // Null check in case refs aren't set yet
    if (!first || !last) {
      return noop;
    }

    const currentId = id.current;
    let cleanup = noop;

    // If the trap is active, we want to handle a couple things:
    //
    // 1. Remove any previous keydown event listener from a former focus trap
    //    to prevent nested focus traps from overlapping one another. For
    //    example, if a user opens a drawer with a focus trap and then opens a
    //    modal from the drawer with its own focus trap, we only want the user
    //    to use the modal's focus trap since it's the latest.
    //
    // 2. Setup the keydown event listener to handle tab and escape
    //
    // 3. Create a new trap object with the following properties:
    //    - eventListener: The keydown event listener that we can reference to
    //                     when removing the trap
    //
    //    - first: The first tabbable element in the trap
    //
    //    - last: The last tabbable element in the trap
    //
    //    - returnTo: The element that triggered the current trap, which we can
    //                redirect the user's focus to once the trap is closed
    //
    // 4. Push the trap object to our map, focus the user to the first
    //    tabbable element, and add the event listener
    if (activeState) {
      // Get the active element that triggered this focus trap to return the
      // user to once the trap is cancelled
      const trigger = document.activeElement;
      const lastTrap = getLastEntry(map);

      // If there's another focus trap, remove its event listener from the
      // document
      if (lastTrap) {
        document.removeEventListener('keydown', lastTrap.eventListener);
      }

      const handleTab = (e) => {
        // If the event target is the last tabbable element in the trap, we
        // circle the user back to the first tabbable element
        if (e.target === last && !e.shiftKey) {
          e.stopPropagation();
          e.preventDefault();
          focusElement(first);
          // Else if the event target is the first tabbable element in the trap,
          // and the user shift+tab's the focus backwards, we circle the user
          // back to the last tabbable element
        } else if (e.target === first && e.shiftKey) {
          e.stopPropagation();
          e.preventDefault();
          focusElement(last);
        }
      };

      const handleEscape = (e) => {
        focusElement(trigger);

        if (lastTrap) {
          document.addEventListener('keydown', lastTrap.eventListener);
        }

        onEscape(e);
      };

      const handleKeyDown = (e) => {
        if (e.key === TAB) {
          handleTab(e);
        } else if (e.key === ESC) {
          handleEscape(e);
        }
      };

      document.addEventListener('keydown', handleKeyDown);

      map.set(id.current, {
        eventListener: handleKeyDown,
        first,
        last,
        returnTo: trigger,
      });

      cleanup = () => document.removeEventListener('keydown', handleKeyDown);
    }

    return () => map.delete(currentId) && cleanup();
  }, [activeState, first, last, onEscape]);
}
